import React, { useCallback, useEffect } from "react";
import { DOT, DOTS, STEP_INDICATOR_WRAP } from "../styles/constants";
import { toKey } from "../components/editModalFunctions";
import type { FormState } from "../App";
import { resolveProductTypeSelection } from "../lib/productTypeSelection";

type AiSuggestions = {
  manufacturer?: string | null;
  model?: string | null;
  productType?: string | null;
  loading: boolean;
  error?: string;
};

type Params = {
  currentStep: number;
  setCurrentStep: (v: number) => void;
  totalSteps: number;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  ensureDraftItem: () => Promise<any>;
  draftItemId: string | null;
  updateDraftMeta: (v: { wizardStep: number }) => Promise<void>;
  aiSuggestions: AiSuggestions;
  setAiSuggestions: React.Dispatch<React.SetStateAction<AiSuggestions>>;
  manufacturerList: string[];
  modelList: string[];
  rpTypeOpts: { id: string; label: string }[];
  setShowNewModelInput: (v: boolean) => void;
  setNewModel: (v: string) => void;
  showNewModelInput: boolean;
  newModel: string;
  userEditedModelRef: React.MutableRefObject<boolean>;
  userEditedManufacturerRef: React.MutableRefObject<boolean>;
  fetchModelsFor: (manufacturer: string) => Promise<void>;
  suggestItemDetailsCallable: (body: any) => Promise<any>;
};

export function useWizardNavigation({
  currentStep,
  setCurrentStep,
  totalSteps,
  form,
  setForm,
  ensureDraftItem,
  draftItemId,
  updateDraftMeta,
  aiSuggestions,
  setAiSuggestions,
  manufacturerList,
  modelList,
  rpTypeOpts,
  setShowNewModelInput,
  setNewModel,
  showNewModelInput,
  newModel,
  userEditedModelRef,
  userEditedManufacturerRef,
  fetchModelsFor,
  suggestItemDetailsCallable,
}: Params) {
  const nextStep = useCallback(async () => {
    try { console.log('[Wizard] nextStep', { currentStep, hasSerial: !!(form?.serial || '').trim() }); } catch (e) { void e; }
    if (currentStep === 1 && !draftItemId) {
      await ensureDraftItem();
    }

    if (currentStep === 3) {
      const __rawSerial = String(form.serial || '').trim();
      const __serialLen = __rawSerial.replace(/\s+/g, '').length;
      if (__serialLen <= 6) {
        try { console.debug('[AI] skip: short serial (<=6)', { serial: __rawSerial, serialLen: __serialLen }); } catch (e) { void e; }
      } else {
        setAiSuggestions({ loading: true });
        try { console.log('[AI] calling suggestItemDetails', { serial: form.serial, ptCount: Array.isArray(rpTypeOpts) ? rpTypeOpts.length : 0 }); } catch (e) { void e; }
        try {
          const res = await suggestItemDetailsCallable({
            serial: form.serial,
            manufacturer: form.manufacturer || undefined,
            productTypes: rpTypeOpts.map(t => t.label)
          });
          if (res.data.ok) {
            try { console.log('[AI] ok', res.data); } catch (e) { void e; }
            setAiSuggestions({
              loading: false,
              manufacturer: (res.data as any).suggestedManufacturer ?? null,
              model: (res.data as any).suggestedModel ?? null,
              productType: (res.data as any).suggestedProductType ?? null,
            });
            setForm(p => {
              const pick = (list: any[] | undefined, s: string | null | undefined, fb: string) => {
                if (!s) return fb;
                const found = (list || []).find((x: any) => toKey(String(x)) === toKey(String(s)));
                return (found ?? s) as string;
              };
              const exactManu = pick(manufacturerList as any, res.data.suggestedManufacturer as any, p.manufacturer);
              const exactModel = pick(modelList as any, res.data.suggestedModel as any, p.model);
              const exactPT = pick((rpTypeOpts || []).map((t: any) => t.label), res.data.suggestedProductType as any, p.productType);
              const productTypeSelection = resolveProductTypeSelection(rpTypeOpts, exactPT || p.productType);
              return {
                ...p,
                manufacturer: exactManu || p.manufacturer,
                model: exactModel || p.model,
                ...productTypeSelection,
              };
            });
          }
        } catch (e: any) {
          console.error('[AI] Fel vid anrop:', e);
          alert(`AI-fel: ${e.message}`);
          setAiSuggestions({ loading: false, error: e.message });
        }
      }
    }

    const newStep = Math.min(currentStep + 1, totalSteps);
    setCurrentStep(newStep);
    await updateDraftMeta({ wizardStep: newStep });
  }, [aiSuggestions, currentStep, draftItemId, ensureDraftItem, form.manufacturer, form.model, form.productType, form.serial, manufacturerList, modelList, rpTypeOpts, setAiSuggestions, setCurrentStep, setForm, suggestItemDetailsCallable, totalSteps, updateDraftMeta]);

  const prevStep = useCallback(async () => {
    const newStep = Math.max(currentStep - 1, 1);
    setCurrentStep(newStep);
    await updateDraftMeta({ wizardStep: newStep });
  }, [currentStep, setCurrentStep, updateDraftMeta]);

  const stepValid: boolean =
    (currentStep === 1 && form.orderNumber.trim() !== "") ||
    (currentStep === 2 && form.customer.trim() !== "") ||
    (currentStep === 3 && form.serial.trim() !== "") ||
    (currentStep === 4 && form.manufacturer.trim() !== "") ||
    (currentStep === 5 && form.model.trim() !== "") ||
    (currentStep === 6 && form.productType.trim() !== "") ||
    (currentStep === 7 && !!form.warrantyStartDate) ||
    currentStep === 8 ||
    (currentStep === 9 && (!form.customer || form.articleNumber.trim() !== "")) ||
    currentStep === 10 ||
    currentStep === 11;

  const StepIndicator: React.FC = () => (
    <div style={STEP_INDICATOR_WRAP}>
      <div style={{ fontSize: 14, marginBottom: 8 }}>
        Steg {currentStep} av {totalSteps}
      </div>
      <div style={DOTS}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={DOT(i + 1 === currentStep)} />
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    if (currentStep >= 4 && aiSuggestions?.manufacturer && !form.manufacturer) {
      if (userEditedManufacturerRef.current) return;
      setForm((p) => ({ ...p, manufacturer: aiSuggestions.manufacturer! }));
    }
  }, [aiSuggestions?.manufacturer, currentStep, form.manufacturer, setForm, userEditedManufacturerRef]);

  useEffect(() => {
    if (currentStep >= 5 && aiSuggestions?.model && !form.model) {
      setForm((p) => ({ ...p, model: aiSuggestions.model! }));
    }
  }, [aiSuggestions?.model, currentStep, form.model, setForm]);

  useEffect(() => {
    if (currentStep >= 6 && aiSuggestions?.productType && !form.productType) {
      setForm((p) => ({
        ...p,
        ...resolveProductTypeSelection(rpTypeOpts, aiSuggestions.productType!),
      }));
    }
  }, [aiSuggestions?.productType, currentStep, form.productType, rpTypeOpts, setForm]);

  useEffect(() => {
    if (currentStep === 5 && aiSuggestions?.model) {
      if (userEditedModelRef.current) return;
      const exists = modelList.some((m) => toKey(m) === toKey(aiSuggestions.model!));
      if (!exists) {
        setShowNewModelInput(true);
        setNewModel(aiSuggestions.model!);
        try { console.debug('[AI:prefill-model] set', { aiModel: aiSuggestions.model }); } catch (e) { void e; }
      }
    }
  }, [aiSuggestions?.model, currentStep, modelList, setNewModel, setShowNewModelInput, userEditedModelRef]);

  useEffect(() => {
    fetchModelsFor(form.manufacturer);
    if (!aiSuggestions?.model) {
      setForm((prev) => ({ ...prev, model: "" }));
    }
    if (!aiSuggestions?.model) setShowNewModelInput(false);
    if (!aiSuggestions?.model) setNewModel("");
    userEditedModelRef.current = false;
  }, [aiSuggestions?.model, fetchModelsFor, form.manufacturer, setForm, setNewModel, setShowNewModelInput, userEditedModelRef]);

  useEffect(() => {
    if (currentStep === 5 && showNewModelInput && (!newModel || !newModel.trim()) && aiSuggestions?.model) {
      if (userEditedModelRef.current) return;
      try { console.debug('[AI:prefill-model] safety', { aiModel: aiSuggestions.model }); } catch (e) { void e; }
      setNewModel(aiSuggestions.model);
    }
  }, [aiSuggestions?.model, currentStep, newModel, setNewModel, showNewModelInput, userEditedModelRef]);

  useEffect(() => {
    if (currentStep === 4) {
      try { console.debug('[AI:render] step4', { manu: aiSuggestions?.manufacturer, has: !!aiSuggestions?.manufacturer }); } catch (e) { void e; }
    }
  }, [aiSuggestions?.manufacturer, currentStep]);
  useEffect(() => {
    if (currentStep === 5) {
      try { console.debug('[AI:render] step5', { showNewModelInput, newModel, aiModel: aiSuggestions?.model }); } catch (e) { void e; }
    }
  }, [aiSuggestions?.model, currentStep, newModel, showNewModelInput]);
  useEffect(() => {
    if (currentStep === 6) {
      try { console.debug('[AI:render] step6', { pt: aiSuggestions?.productType, has: !!aiSuggestions?.productType }); } catch (e) { void e; }
    }
  }, [aiSuggestions?.productType, currentStep]);

  return {
    nextStep,
    prevStep,
    stepValid,
    StepIndicator,
  };
}
